import Vue from "vue";
import VueCookies from "vue-cookies";
Vue.use(VueCookies);

const APP_USER_AGENT = "hmcAppUserAgent";

/**
 * @returns if user uses application
 */
export function isApplication() {
  return Vue.$cookies.get("cookie-user-agent") === APP_USER_AGENT;
}

/**
 * The outdated application version never sets cookie-user-agent at all. It
 * only replaces the whole user agent with APP_USER_AGENT, and nothing on the
 * web side derives the cookie from that header. The current version is the
 * one that sends a real browser user agent and sets the cookie by itself.
 *
 * So the user agent is the only trace the outdated version leaves behind,
 * and it is what the version is matched by here. Note the consequence:
 * isApplication() is false inside the outdated version, so anything that has
 * to cover both versions has to check for this one as well.
 *
 * @returns if user uses an application version that has to be updated
 * from the store.
 */
export function isOutdatedApplication() {
  return (navigator.userAgent || "").includes(APP_USER_AGENT);
}

const APP_STORE_URL =
  "https://apps.apple.com/il/app/mymedical-doctors/id1619098479";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.hmczimun.hmcappliction";

/**
 * Outdated application versions replace the whole user agent, so it holds no
 * platform information at all. That override leaves navigator.platform and
 * navigator.vendor intact, which is why the device is matched by them here.
 *
 * @returns if the current device is an iOS one.
 */
export function isIOSDevice() {
  const platform = navigator.platform || "";

  return (
    /iPad|iPhone|iPod/.test(platform) ||
    // iPadOS 13+ reports itself as a mac with a touch screen
    (platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
    /Apple/.test(navigator.vendor || "")
  );
}

/**
 * @returns the store url of the current device.
 */
export function getAppStoreUrl() {
  return isIOSDevice() ? APP_STORE_URL : PLAY_STORE_URL;
}

/**
 * @returns if window is smaller then 1200px.
 */
export function isMobile() {
  return window.innerWidth < 1200;
}

/**
 * @returns mobile or desktop delay integer
 */
export function backClickTimeout() {
  return isMobile() ? 600 : 1500;
}

/**
 * @returns if event is number or not
 */
export function numbersOnly(ev) {
  ev = ev ? ev : window.event;
  var charCode = ev.which ? ev.which : ev.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
    ev.preventDefault();
  } else {
    return true;
  }
}

/**
 * @returns menu list
 */
export const menuList = [
  {
    path: "/dashboard",
    title: "ניהול מרפאה"
  },
  {
    path: "/dashboard/surgeries",
    title: "ניהול חדרי ניתוח/פעולות"
  },
  {
    path: "/surg-add-block",
    title: "הזמנת בלוק ניתוחים"
  },
  {
    path: "/table/appointment",
    title: "רשימת מטופלים - מרפאה"
  },
  {
    path: "/table/surgery",
    title: "רשימת מטופלים - ניתוח/פעולה"
  },
  {
    path: "/table/ivf",
    title: "תוכניות טיפול IVF",
    type: "ivf",
    requiresIVF: true
  },
  {
    path: "/table/pathology",
    title: "דוחות פתולוגיה",
    type: "pathology"
  },
  {
    path: "/table/genetics",
    title: "דוחות מעבדה גנטית",
    type: "genetics"
  },
  {
    isAccordion: true,
    title: "כלים ועזרים",
    children: [
      {
        path:
          "https://selfservice.hmc.co.il/m/mSelfService?operation=reset&init=true",
        title: "איפוס סיסמה לקמיליון",
        icon: "lock",
        target: "_blank"
      },
      {
        path: "/profile",
        title: "פרופיל רופא",
        icon: "pen"
      },
      {
        path: "/license-plate",
        title: "הוספה/החלפת רכב",
        icon: "card"
      },
      {
        path: "/cellosign-iframe/secretary-access",
        title: "פתיחת גישה למזכירת מרפאה",
        icon: "contact"
      },
      {
        action: "uploadPolicies",
        title: "טעינת קובץ ביטוח",
        icon: "upload"
      }
    ]
  },
  {
    path: "/cellosign",
    title: "טפסי הסכמה לפעולה",
    requiresIVF: true
  },
  {
    path: "/cellosign-iframe/exception-committee",
    title: "וועדת חריגים"
  },
  {
    path:
      "https://api.whatsapp.com/send/?phone=97299594809&text=שלום&type=phone_number&app_absent=0",
    title: "מוקד Fixer",
    target: "_blank"
  }
];

export const menuListMobile = [
  {
    isAccordion: true,
    title: "ניהול מרפאה",
    children: [
      {
        path: "/dashboard",
        title: "תצוגת יומן",
        icon: "date"
      },
      {
        path: "/dashboard/list",
        title: "רשימת תורים",
        icon: "list"
      }
    ]
  },
  {
    isAccordion: true,
    title: "ניתוחים",
    children: [
      {
        path: "/dashboard/surgeries",
        title: "תצוגת יומן",
        icon: "date"
      },
      {
        path: "/dashboard/surgeries/list",
        title: "רשימת ניתוחים",
        icon: "list"
      }
    ]
  },
  {
    path: "/surg-add-block",
    title: "הזמנת בלוק ניתוחים"
  },
  {
    isAccordion: true,
    title: "רשימת מטופלים",
    children: [
      {
        path: "/table/appointment",
        title: "מרפאה",
        icon: "list"
      },
      {
        path: "/table/surgery",
        title: "ניתוח/פעולה",
        icon: "list"
      },
      {
        path: "/table/ivf",
        title: "תוכניות טיפול IVF",
        icon: "list",
        requiresIVF: true
      }
    ]
  },
  {
    path: "/table/pathology",
    title: "דוחות פתולוגיה",
    type: "pathology"
  },
  {
    path: "/table/genetics",
    title: "דוחות מעבדה גנטית",
    type: "genetics"
  },
  {
    isAccordion: true,
    title: "כלים ועזרים",
    children: [
      {
        path:
          "https://selfservice.hmc.co.il/m/mSelfService?operation=reset&init=true",
        title: "איפוס סיסמה לקמיליון",
        icon: "lock",
        target: "_blank"
      },
      {
        path: "/profile",
        title: "פרופיל רופא",
        icon: "pen"
      },
      {
        path: "/license-plate",
        title: "הוספה/החלפת רכב",
        icon: "card"
      },
      {
        path: "/cellosign-iframe/secretary-access",
        title: "פתיחת גישה למזכירת מרפאה",
        icon: "contact"
      },
      {
        action: "uploadPolicies",
        title: "טעינת קובץ ביטוח",
        icon: "upload"
      }
    ]
  },
  {
    path: "/cellosign",
    title: "טפסי הסכמה לפעולה",
    requiresIVF: true
  },
  {
    path: "/cellosign-iframe/exception-committee",
    title: "וועדת חריגים"
  },
  {
    path:
      "https://api.whatsapp.com/send/?phone=97299594809&text=שלום&type=phone_number&app_absent=0",
    title: "מוקד Fixer",
    target: "_blank"
  }
];

export function convertToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      let encoded = reader.result.toString().replace(/^data:(.*,)?/, "");
      if (encoded.length % 4 > 0) {
        encoded += "=".repeat(4 - (encoded.length % 4));
      }
      resolve(encoded);
    };
    reader.onerror = error => reject(error);
  });
}

export async function uploadDocuments(
  files,
  appointmentId,
  type = "medicaldocuments-patient",
  isSurgery = false
) {
  const filesArray = [];
  for (let i = 0; i < files.length; i++) {
    if (files[i] instanceof File) {
      const base64 = await convertToBase64(files[i]);
      filesArray.push({
        filename: files[i].name,
        mime: files[i].type,
        base64
      });
    }
  }
  if (!filesArray.length) return [];
  try {
    const res = await this.$API.uploadFile(
      appointmentId,
      "new",
      null,
      "",
      filesArray,
      isSurgery,
      type
    );
    if (res?.status === "ok") {
      if (Array.isArray(res?.errordesc?.document_ids)) {
        return res.errordesc.document_ids;
      }
      if (res?.errordesc?.document_id) {
        return [res.errordesc.document_id];
      }
    }
    return [];
  } catch (error) {
    if (error.errordesc) return { error: error.errordesc };
    return { error: "התרחשה שגיאה בהעלאת הקובץ" };
  }
}
